import React, { memo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { X } from "lucide-react";
import { AiOutlineSync } from 'react-icons/ai';
import { useAppDispatch, useAppSelector } from "@common/reducers/hooks/store.hook";
import { setIsRecurring, setRruleset } from "@common/reducers/ScheduledMessageEditorReducer";
import RecurrenceOptionsPopover from "./recurrence-options-popover";
import { cn } from "@/lib/utils";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

import type { Frequency } from 'rrule';

type MessageRecurrenceOptionsProps = {
    /** FE#240: restrict the frequency dropdown (default: all). */
    allowedFrequencies?: Frequency[];
    /**
     * FE#286: z-index for the recurrence popover. Default 91000 works everywhere
     * EXCEPT inside a ModalOverlay (z 2147483000), where callers must pass
     * MODAL_OVERLAY_POPOVER_Z so the popover isn't buried behind the modal.
     */
    zIndex?: number;
    /**
     * Icon-only trigger (the compose dialog's compact layout puts it at the end
     * of the "will be sent on" strip). The label moves to the tooltip/aria; when
     * set, a small × badge in the corner clears it.
     */
    iconOnly?: boolean;
};

const MessageRecurrenceOptions = ({ allowedFrequencies, zIndex = 91000, iconOnly = false }: MessageRecurrenceOptionsProps = {}) => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const [open, setOpen] = useState(false);
    // The hint tooltip must not sit over the popover it describes. Radix re-opens it when focus
    // returns to the trigger, so it stays suppressed after a popover open/close until the pointer
    // leaves the control.
    const [tipOpen, setTipOpen] = useState(false);
    const [tipSuppressed, setTipSuppressed] = useState(false);
    const changePopover = (o: boolean) => {
        if (o && !canOpenRecurrence) return;
        setTipSuppressed(true);
        setOpen(o);
    };

    // Read state directly from Redux
    const isRecurring = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isRecurring);
    const dueDate = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.dueDate);

    // BE#657 R3-M1 — a recurrence rule is anchored on the message's dueDate (its DTSTART). With no
    // dueDate (a fresh compose / wizard "Schedule for later" before a date is picked, now that the
    // pickers no longer self-seed "now") `new RRule({dtstart: <invalid>})` throws "Invalid options:
    // dtstart" and the recurrence is silently dropped. Gate the control on a valid dueDate so the
    // user is told to pick a date first instead of setting a recurrence that never applies. An
    // already-recurring message necessarily has a dueDate, so editing stays available.
    const hasValidDueDate = !!dueDate && !Number.isNaN(new Date(dueDate as string).getTime());
    const canOpenRecurrence = isRecurring || hasValidDueDate;

    const handleRecurrenceClose = () => {
        changePopover(false);
    };

    const handleIsRecurranceCancel = () => {
        dispatch(setIsRecurring(false));
        dispatch(setRruleset(''));
    };

    return (
        <div className={iconOnly ? "flex items-center" : "flex items-center justify-center h-12 min-h-12"}>
            <div className="relative w-full">
                <Tooltip open={tipOpen && !open && !tipSuppressed} onOpenChange={setTipOpen}>
                    {/* R4-m1 — the tooltip must anchor to a real DOM element that always receives
                        hover. Anchoring it on the Popover directly dropped the tooltip trigger props
                        (the Popover provider absorbed them, so only the Popover's own attrs reached the
                        Button and the tooltip never attached). A <span> wrapper is the hoverable anchor;
                        combined with aria-disabled (not `disabled`) on the Button, the "pick a date
                        first" tooltip now shows on the greyed control. */}
                    <TooltipTrigger asChild>
                        <span className="block w-full" tabIndex={canOpenRecurrence ? undefined : 0} onPointerLeave={() => { setTipSuppressed(false); setTipOpen(false); }}>
                        <Popover open={open} onOpenChange={changePopover}>
                            <PopoverTrigger asChild>
                                <Button
                                    // BE#657 R4-m1 — use aria-disabled, NOT the `disabled` attr: shadcn's
                                    // `disabled:pointer-events-none` would swallow hover, so the
                                    // "pick a date first" tooltip never opened on the greyed button. With
                                    // aria-disabled the button still receives hover (tooltip shows) while
                                    // onOpenChange refuses to open the popover and the class greys it out.
                                    aria-disabled={!canOpenRecurrence}
                                    variant={isRecurring ? "default" : iconOnly ? "ghost" : "outline"}
                                    aria-label={isRecurring ? t('messageScheduling.editRecurrence') : t('messageScheduling.setRecurrence')}
                                    className={iconOnly
                                        ? cn(
                                            "h-8 w-8 min-h-8 min-w-0 p-0 rounded-full border transition-all duration-200",
                                            isRecurring
                                                ? "border-[var(--cmp-brand)] bg-[var(--cmp-brand-soft)] text-[var(--cmp-brand-ink)]"
                                                : "border-transparent bg-transparent text-[var(--tasks-text-secondary)] hover:bg-[var(--color-surface-muted)]",
                                            // aria-disabled visual (pointer-events INTACT so the tooltip fires)
                                            !canOpenRecurrence && "opacity-50 cursor-not-allowed",
                                        )
                                        : cn(
                                        "text-sm font-medium rounded-[20px] px-4 py-1.5 h-8 min-h-8",
                                        "border border-[var(--color-border)]",
                                        "w-full min-w-[110px] sm:min-w-[140px] max-w-[200px]",
                                        "overflow-hidden whitespace-nowrap",
                                        "transition-all duration-200",
                                        // aria-disabled visual (pointer-events INTACT so the tooltip fires)
                                        !canOpenRecurrence && "opacity-50 cursor-not-allowed hover:border-[var(--color-border)]",
                                        // House style: colour is a small saturated MARK, never a
                                        // large fill. The active state used to paint the whole pill
                                        // near-black (#0a0a0a in BOTH themes, with --tasks-board-bg
                                        // as the label — a pair nobody had measured). It is now a
                                        // tinted pill with a brand hairline and brand INK, so the
                                        // saturation is carried by the text and the icon.
                                        isRecurring && "border-[var(--cmp-brand)] bg-[var(--cmp-brand-soft)] text-[var(--cmp-brand-ink)]",
                                        !isRecurring && "bg-transparent text-[var(--tasks-text-secondary)]",
                                        "hover:border-[var(--color-accent)]"
                                    )}
                                >
                                    {!iconOnly && isRecurring && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleIsRecurranceCancel();
                                                    }}
                                                    /* `me-2`, not `mr-2`: this app runs in Hebrew/RTL
                                                       and a physical margin-right does not mirror. */
                                                    className="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer transition-all duration-300 me-2 hover:opacity-60"
                                                >
                                                    <X className="h-3.5 w-3.5 text-[var(--cmp-brand-ink)] transition-colors duration-300" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>{t('messageScheduling.cancelRecurrence', 'Cancel recurrence')}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    )}
                                    {!iconOnly && (isRecurring ? t('messageScheduling.editRecurrence') : t('messageScheduling.setRecurrence'))}
                                    <AiOutlineSync
                                        // No margin needed: the button is flex with `gap-2`, which is
                                        // direction-agnostic.
                                        //
                                        // (The comment that used to sit here claimed `ms-*` compiles to a
                                        // physical margin-left in this build. That is false and was worth
                                        // checking rather than inheriting: compiling `me-2 ms-2 mr-2` through
                                        // this repo's own @tailwindcss/postcss v4.1 emits
                                        // `margin-inline-end` / `margin-inline-start` / `margin-right`
                                        // respectively. The logical utilities ARE logical here.)
                                        className="h-[18px] w-[18px] transition-all duration-200"
                                    />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                align="start"
                                side="top"
                                className="p-0 rounded-2xl w-[360px]"
                                zIndex={zIndex}
                            >
                                <RecurrenceOptionsPopover
                                    onClose={handleRecurrenceClose}
                                    allowedFrequencies={allowedFrequencies}
                                />
                            </PopoverContent>
                        </Popover>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>{!canOpenRecurrence
                            ? t('messageScheduling.pickDateBeforeRecurrence', 'Pick a date and time first, then set a recurrence')
                            : isRecurring
                                ? t('messageScheduling.recurrenceHintOn', 'Edit recurrence settings')
                                : t('messageScheduling.recurrenceHintOff', 'Set up a recurring schedule for your message')}</p>
                    </TooltipContent>
                </Tooltip>
                {iconOnly && isRecurring && (
                    <button
                        type="button"
                        onClick={handleIsRecurranceCancel}
                        aria-label={t('messageScheduling.cancelRecurrence', 'Cancel recurrence')}
                        className="absolute -top-1 -end-1 grid h-4 w-4 place-items-center rounded-full bg-[var(--cmp-brand-ink)] text-white hover:opacity-80"
                    >
                        <X className="h-2.5 w-2.5" />
                    </button>
                )}
            </div>
        </div>
    )
}

export default memo(MessageRecurrenceOptions);